<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  branding: { type: Object, default: () => ({}) },
});
const { t } = useI18n();
const isDark = ref(false);

const ICON_MAP = {
  'message-circle': 'i-lucide-message-circle',
  users: 'i-lucide-users',
  zap: 'i-lucide-zap',
  shield: 'i-lucide-shield',
  globe: 'i-lucide-globe',
  headphones: 'i-lucide-headphones',
  'bar-chart': 'i-lucide-bar-chart',
  clock: 'i-lucide-clock',
  heart: 'i-lucide-heart',
  star: 'i-lucide-star',
  'check-circle': 'i-lucide-check-circle',
  send: 'i-lucide-send',
  inbox: 'i-lucide-inbox',
  smartphone: 'i-lucide-smartphone',
  mail: 'i-lucide-mail',
  bell: 'i-lucide-bell',
  settings: 'i-lucide-settings',
  layers: 'i-lucide-layers',
  award: 'i-lucide-award',
  'trending-up': 'i-lucide-trending-up',
};
const featureIcon = name => ICON_MAP[name] || 'i-lucide-message-circle';

const logoSrc = computed(() =>
  isDark.value
    ? props.branding.logoDarkUrl || props.branding.logoUrl || null
    : props.branding.logoUrl || null
);

const overlayStyle = computed(() => ({
  backgroundColor: props.branding.loginOverlayColor || '#1f93ff',
  opacity: Number(props.branding.loginOverlayOpacity ?? 80) / 100,
}));

const bgStyle = computed(() =>
  props.branding.loginBgImageUrl
    ? {
        backgroundImage: `url(${props.branding.loginBgImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : { backgroundColor: '#0f172a' }
);

const formRowAlign = computed(() => {
  const pos = props.branding.loginFormPosition || 'left';
  if (pos === 'right') return 'justify-end';
  if (pos === 'center') return 'justify-center';
  return 'justify-start';
});

const features = computed(() =>
  (props.branding.loginFeatures || []).filter(f => f.title || f.description)
);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('REVENDA.BRANDING.PREVIEW.TITLE') }}
      </span>
      <button
        type="button"
        data-testid="preview-dark-toggle"
        class="px-2 py-1 text-xs rounded-md bg-n-alpha-black2 text-n-slate-11"
        @click="isDark = !isDark"
      >
        {{
          isDark
            ? t('REVENDA.BRANDING.PREVIEW.DARK')
            : t('REVENDA.BRANDING.PREVIEW.LIGHT')
        }}
      </button>
    </div>

    <!-- Mini tela de login -->
    <div
      class="relative overflow-hidden rounded-xl border border-n-weak aspect-[16/10]"
      :style="bgStyle"
    >
      <div
        data-testid="preview-overlay"
        class="absolute inset-0"
        :style="overlayStyle"
      />
      <div
        data-testid="preview-form-row"
        class="relative flex w-full h-full p-4"
        :class="formRowAlign"
      >
        <div
          class="flex flex-col items-center gap-2 p-3 rounded-xl shadow w-44 bg-white/40 backdrop-blur-xl border border-black/10"
        >
          <!-- Logo -->
          <img
            v-if="logoSrc"
            data-testid="preview-logo"
            :src="logoSrc"
            alt="logo"
            class="object-contain h-6 mb-1"
          />
          <!-- Title -->
          <span
            data-testid="preview-login-title"
            class="text-sm font-semibold text-center leading-tight"
            :style="{ color: branding.loginTitleColor || '#0f172a' }"
          >
            {{
              branding.loginPageTitle ||
              t('REVENDA.BRANDING.PREVIEW.SAMPLE_TITLE')
            }}
          </span>
          <!-- Description -->
          <span
            data-testid="preview-login-description"
            class="text-[10px] text-center leading-snug"
            :style="{ color: branding.loginDescriptionColor || '#475569' }"
          >
            {{
              branding.loginPageDescription ||
              t('REVENDA.BRANDING.PREVIEW.SAMPLE_DESC')
            }}
          </span>
          <!-- Fake input bars (decorative) -->
          <div
            class="w-full h-5 mt-1 rounded-lg bg-white/60 border border-black/10"
          />
          <div
            class="w-full h-5 rounded-lg bg-white/60 border border-black/10"
          />
          <!-- Sign in button -->
          <div
            class="w-full h-6 rounded-lg text-white text-[10px] flex items-center justify-center mt-1"
            :style="{ backgroundColor: branding.brandColor || '#1f73b7' }"
          >
            {{ t('REVENDA.BRANDING.PREVIEW.SAMPLE_LOGIN_BTN') }}
          </div>
          <!-- Features -->
          <ul
            v-if="features.length"
            class="flex flex-col gap-1 mt-1 w-full pt-2 border-t border-black/10"
          >
            <li
              v-for="(f, i) in features"
              :key="i"
              data-testid="preview-feature"
              class="flex items-center gap-1 text-[10px] text-n-slate-11"
            >
              <Icon :icon="featureIcon(f.icon)" class="size-3" />
              <span class="truncate">{{ f.title }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Mini chip de dashboard -->
    <div
      class="flex gap-2 p-2 rounded-xl border border-n-weak"
      :class="isDark ? 'bg-n-slate-2' : 'bg-white'"
    >
      <div
        class="flex flex-col items-center w-10 gap-2 py-2 rounded-lg"
        :class="isDark ? 'bg-n-slate-3' : 'bg-n-slate-2'"
      >
        <img
          v-if="logoSrc"
          :src="logoSrc"
          alt="logo"
          class="object-contain w-6 h-6"
        />
        <span
          data-testid="preview-accent-button"
          class="w-6 h-6 rounded-md"
          :style="{ backgroundColor: branding.brandColor || '#1f73b7' }"
        />
      </div>
      <div class="flex-1 text-xs text-n-slate-11">
        {{ t('REVENDA.BRANDING.PREVIEW.DASHBOARD_HINT') }}
      </div>
    </div>
  </div>
</template>
